import comingsoonImage from "../../assets/images/construction.jpg";
import StoreCreation from "../../components/settings/system-settings/store-creation";
import PermissionSettings from "../../components/settings/permission-settings";
import {
  Activity,
  Archive,
  BadgeCheck,
  BadgePercent,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CalendarCheck,
  CalendarClock,
  CreditCard,
  Database,
  Download,
  Factory,
  FileText,
  FileUp,
  FlaskConical,
  Gift,
  Hash,
  Inbox,
  KeyRound,
  Layers,
  LayoutGrid,
  LayoutTemplate,
  Link2,
  ListOrdered,
  MapPin,
  Megaphone,
  MessageCircle,
  Monitor,
  NotebookPen,
  Package,
  Percent,
  Printer,
  ReceiptText,
  RefreshCw,
  Replace,
  Ruler,
  Server,
  Settings,
  ShieldCheck,
  ShieldPlus,
  SlidersHorizontal,
  Split,
  Stethoscope,
  Store,
  Tag,
  Tags,
  Truck,
  UserCog,
  Users,
  Wallet,
} from "lucide-react";
import { isAdmin } from "../../redux/config.js";
const NoChild = () => {
  return (
    <div className="h-full flex items-center justify-center">
      <img alt="Coming soon" src={comingsoonImage} />
    </div>
  );
};

// `icon` is a lucide component (not an element) so the page can size it and
// colour it with the section's `tone` (a --reports-<tone>_text/_bg pair from index.css).
const settingsData = [
  {
    icon: Settings,
    tone: "shade1",
    title: "System Settings",
    desc: "Store, numbering, printing, sync and app updates",
    path: "settings/system-settings",
    childrens: [
      {
        icon: Building2,
        title: "Company Level Settings",
        desc: "Company-wide defaults shared by every store",
        path: "settings/system-settings/general-settings/company-level-settings ",
        input: <NoChild />,
        module_name: "company_level_setting",
      },
      {
        icon: Store,
        title: "Store Details",
        desc: "Store name, address, GST and contact details",
        path: "settings/system-settings/general-settings/store-creation",
        input: <StoreCreation />,
        module_name: "store_details",
      },
      {
        icon: BadgeCheck,
        title: "Store Licensing",
        desc: "Drug and trade licences for this store",
        path: "settings/system-settings/general-settings/store-licensing ",
        input: <NoChild />,
        module_name: "store_licensing",
      },
      {
        icon: Hash,
        title: "Document Numbering Series",
        desc: "Prefixes and running numbers for invoices, orders and returns",
        path: "/settings/document-numbering-series",
        input: null,
        module_name: "document_numbering",
      },
      {
        icon: ListOrdered,
        title: "Custom Document Numbering Series",
        desc: "Your own numbering formats per document type",
        path: "/settings/custom-numbering-series",
        input: null,
        module_name: "document_numbering",
      },
      {
        icon: FileText,
        title: "Print Preferences",
        desc: "Paper size, copies and what each printout includes",
        path: "/settings/print-preference",
        input: null,
        module_name: "print_perfernces",
      },
      {
        icon: Printer,
        title: "Printer Setup",
        badge: "QZ Tray",
        desc: "Choose printers for documents and thermal receipts, test print",
        path: "/settings/printer-setup",
        input: null,
        module_name: "print_perfernces",
      },
      {
        icon: LayoutGrid,
        title: "Module Settings",
        desc: "Configure modules and their options",
        path: "/settings/module-settings",
        input: null,
        module_name: "store_details",
      },
      {
        icon: MessageCircle,
        title: "Follow-up Message",
        badge: "WhatsApp",
        desc: "Message sent to customers whose refills are due",
        path: "/settings/follow-up-message",
        input: null,
        module_name: "store_details",
      },
      {
        icon: Link2,
        title: "Vendor Mapping",
        desc: "Map your vendors",
        path: "/settings/bussiness-catalouge",
        input: null,
        module_name: "customization",
      },
      {
        icon: LayoutTemplate,
        title: "Invoice Builder",
        desc: "Design print formats",
        path: "/settings/invoice-customization",
        input: null,
        module_name: "customization",
      },
      {
        icon: RefreshCw,
        title: "Sync Rule",
        desc: "Sync rule configuration",
        path: "/settings/sync-rule",
        input: null,
        module_name: "store_details",
      },
      {
        icon: Activity,
        title: "Sync Dashboard",
        desc: "Trigger manual sync and view sync status",
        path: "/settings/sync-dashboard",
        input: null,
        module_name: "store_details",
      },
      !isAdmin && {
        icon: Download,
        title: "Software Update",
        desc: "Check the current version and update the application",
        path: "/settings/software-update",
        input: null,
      },
    ].filter(Boolean),
  },
  {
    icon: Users,
    tone: "shade2",
    title: "User Settings",
    desc: "Users, roles, permissions and personal preferences",
    path: "settings/system-settings",
    childrens: [
      {
        icon: UserCog,
        title: "User Info",
        desc: "Add and manage user accounts",
        input: null,
        path: "/user",
        module_name: "user_info",
      },
      {
        icon: Users,
        title: "Role and Department",
        desc: "Group users by role and department",
        input: null,
        path: "/settings/department-and-role-setup",
        module_name: "user_info",
      },

      {
        icon: ShieldCheck,
        title: "Authorizations",
        desc: "Control what each role can view, create and edit",
        path: "settings/system-settings/general-settings",
        input: <PermissionSettings />,
        module_name: "authorizations",
      },
      {
        icon: SlidersHorizontal,
        title: "Customization",
        desc: "Choose fields, columns and layouts for each screen",
        path: "/settings/customization",
        input: null,
        module_name: "customization",
      },
      {
        icon: KeyRound,
        title: "Licensing",
        desc: "Your software licence and plan details",
        path: "settings/system-settings/store-details",
        input: <NoChild />,
        module_name: "licensing",
      },

      {
        icon: Monitor,
        title: "Display Settings",
        desc: "Theme and display preferences",
        path: "settings/system-settings",
        input: <NoChild />,
        module_name: "display_setting",
      },
    ],
  },
  {
    icon: Database,
    tone: "shade4",
    title: "Data Setup",
    desc: "Master data used across billing, purchase and inventory",
    path: "settings/system-settings",
    childrens: [
      {
        icon: Percent,
        title: "Tax Setup",
        desc: "Tax setup, tax combination, accounting",
        path: "/settings/tax-setup",
        input: null,
        module_name: "tax_setup",
      },
      {
        icon: ReceiptText,
        title: "TCS - TDS Setup",
        desc: "TCS setup, TDS combination",
        path: "/settings/tcs-tds-setup",
        input: null,
        module_name: "tax_setup",
      },

      {
        icon: Package,
        title: "Item Setup",
        desc: "Item setup, display, warehouse, accounting",
        path: "/settings/item-setup",
        input: null,
        childrens: [],
        module_name: "item_setup",
      },

      {
        icon: Factory,
        title: "Manufacturer Setup",
        desc: "Manufacturer setup, display, warehouse, accounting",
        path: "/settings/manufacturer-setup",
        input: null,
        childrens: [],
        module_name: "manufacturer_setup",
      },
      {
        icon: Truck,
        title: "Shipping Setup",
        desc: "Shipping setup, display, warehouse, accounting",
        path: "/settings/shipping-setup",
        input: null,
        childrens: [],
        module_name: "shipping_setup",
      },
      {
        icon: Ruler,
        title: "Unit Of Measures",
        desc: "Unit of measure, inventory, alerts, display, warehouse, accounting",
        path: "/settings/uom-setup",
        input: null,
        childrens: [],
        module_name: "uom_setup",
      },
      {
        icon: Layers,
        title: "Unit Of Measure Groups",
        desc: "Unit of measure groups, inventory, alerts, display, warehouse, accounting",
        path: "/settings/uom-group-setup",
        input: null,
        childrens: [],
        module_name: "uom_group_setup",
      },
      {
        icon: Replace,
        title: "Alternative Items",
        desc: "Alternative items mapping",
        path: "/settings/alternative-item-setup",
        input: null,
        childrens: [],
        module_name: "uom_group_setup",
      },
      {
        icon: FileUp,
        title: "Import Templates",
        desc: "Create templates for importing CSVs, PDFs or images across modules",
        path: "/settings/import-templates",
        input: null,
        childrens: [],
        module_name: "uom_group_setup",
      },
      {
        icon: Bell,
        title: "Alert Management",
        desc: "Create, customize and maintain alerts within the system",
        input: null,
        path: "/alerts",
        module_name: "alert_management",
      },
      {
        icon: CalendarCheck,
        title: "Schedule Drug",
        desc: "Create, customize and maintain schedule drugs",
        input: null,
        path: "/settings/schedule-drug",
        module_name: "schedule_drug",
      },
      {
        icon: FlaskConical,
        title: "Composition",
        desc: "Create, customize and maintain compositions",
        input: null,
        path: "/settings/composition",
        module_name: "composition_setup",
      },
      {
        icon: Megaphone,
        title: "Lead Source",
        desc: "Create, customize and maintain lead sources",
        input: null,
        path: "/settings/lead",
        module_name: "lead_source",
      },
      {
        icon: CalendarClock,
        title: "Payment Terms",
        desc: "Create, customize and maintain payment terms",
        input: null,
        path: "/settings/payment",
        module_name: "payment_terms",
      },
      {
        icon: CreditCard,
        title: "Payment Type",
        desc: "Create, customize and maintain payment types",
        input: null,
        path: "/settings/payment-type",
        module_name: "payment_terms",
      },
      {
        icon: Tags,
        title: "Party Category",
        desc: "Create, customize and maintain party categories",
        input: null,
        path: "/settings/party",
        module_name: "party_category",
      },
      {
        icon: Stethoscope,
        title: "Specialization & Sub-Specialization",
        desc: "Create, customize and maintain specializations",
        input: null,
        path: "/settings/specialization",
        module_name: "specialization",
      },
      {
        icon: ShieldPlus,
        title: "Insurance Provider",
        desc: "Create, customize and maintain insurance providers",
        input: null,
        path: "/settings/insurance",
        module_name: "insurance_provider",
      },
      {
        icon: MapPin,
        title: "Location",
        desc: "Create, customize and maintain locations",
        input: null,
        path: "/settings/location",
        module_name: "location_setup",
      },
      {
        icon: Server,
        title: "Server",
        desc: "Server setup, display, warehouse, accounting",
        input: null,
        path: "/settings/server",
        module_name: "location_setup",
      },
      {
        icon: Archive,
        title: "Packing",
        desc: "Create, customize and maintain packing",
        input: null,
        path: "/settings/pack-info",
        module_name: "pack_info_setup",
      },
      // Trays are reserved by drop lists / pick lists, which only exist on CPOS.
      isAdmin && {
        icon: Inbox,
        title: "Tray Management",
        desc: "Warehouse trays for drop lists and pick lists: create, rename, inactivate",
        input: null,
        path: "/settings/tray-management",
        module_name: "tray",
      },
    ].filter(Boolean),
  },
  {
    icon: Tag,
    tone: "shade3",
    title: "Discount Settings",
    desc: "Discount rules applied during billing",
    path: "settings/system-settings",
    childrens: [
      {
        icon: Tag,
        title: "Price List Discounts",
        desc: "Discounts set per price list",
        input: null,
        path: "/price-list",
        module_name: "price_list",
      },
      {
        icon: BarChart3,
        title: "Period-Volume Discounts",
        desc: "Discounts by period and quantity bought",
        input: null,
        path: "/period-volume",
        module_name: "period_and_volume_discount",
      },
      {
        icon: BadgePercent,
        title: "Discount groups",
        desc: "Group items under a shared discount",
        input: null,
        path: "/discount-group",
        module_name: "discount_group",
      },
      {
        icon: Megaphone,
        title: "Promotional Offers",
        desc: "Configure auto-add free item promotions based on bill amount",
        path: "/settings/promotional-offers",
        input: null,
        module_name: "promotional_offers",
      },
      {
        icon: Gift,
        title: "Promotions",
        desc: "Loyalty, coupons & schemes — one place to author every discount",
        path: "/settings/promotions",
        input: null,
        // Surfaces when ANY of the three sub-permissions is granted (legacy
        // single-permission deployments still work via loyalty_admin).
        module_name: "loyalty_admin",
      },
    ],
  },
  {
    icon: Wallet,
    tone: "shade6",
    title: "Account Settings",
    desc: "Accounting configuration",
    path: "settings/system-settings",
    childrens: [
      {
        icon: BookOpen,
        title: "Chart of Accounts",
        desc: "Account heads and their hierarchy",
        input: null,
        path: "/chart-of-account",
        module_name: "chart_of_accounts",
      },
      {
        icon: Split,
        title: "Account Allocation",
        desc: "Which accounts each transaction posts to",
        input: null,
        path: "/account-allocation",
        module_name: "account_allocation",
      },
      {
        icon: NotebookPen,
        title: "Journal Entry",
        desc: "Record manual journal entries",
        input: null,
        path: "/journal-entry",
        module_name: "journal_entry",
      },
    ],
  },
  // {
  //   icon: <SmileIcon />,
  //   title: "Data Sync Management",
  //   desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //   path: "settings/system-settings",
  //   childrens: [
  //     {
  //       icon: <SmileIcon />,
  //       title: "Store wise Rules Setup",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "settings/system-settings/store-details",
  //       input: <NoChild />,
  //     },
  //   ],
  // },
  // {
  //   icon: <SmileIcon />,
  //   title: "Communication",
  //   desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //   path: "settings/system-settings",
  //   childrens: [
  //     {
  //       icon: <SmileIcon />,
  //       title: "Whatsapp Api Config",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "/settings/whatsapp-configuration",
  //       input: null,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "Message Api Config",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "/settings/sms-configuration",
  //       input: null,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "SMTP Config",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "/settings/smtp-configuration",
  //       input: null,
  //     },
  //   ],
  // },
  // {
  //   icon: <SmileIcon />,
  //   title: "Payment Settings",
  //   desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //   path: "settings/system-settings",
  //   childrens: [
  //     // {
  //     //     icon: <SmileIcon />,
  //     //     title: "Payment Config",
  //     //     desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //     //     path: "settings/system-settings/store-details",
  //     //     input: <NoChild />,
  //     // },
  //     {
  //       icon: <SmileIcon />,
  //       title: "Razorpay Config",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "/settings/razorpay-configuration",
  //       input: null,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "Stripe Config",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "/settings/stripe-configuration",
  //       input: null,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "Phonepe Config",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "/settings/phonepe-configuration",
  //       input: null,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "Paytm Config",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "/settings/paytm-configuration",
  //       input: null,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "Pine Labs Config",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "/settings/pine-labs-configuration",
  //       input: null,
  //     },
  //   ],
  // },
  // {
  //   icon: <SmileIcon />,
  //   title: "Integrations",
  //   desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //   path: "settings/system-settings",
  //   childrens: [
  //     {
  //       icon: <SmileIcon />,
  //       title: "Hub Spot Setup",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "settings/system-settings/store-details",
  //       input: <NoChild />,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "ERP 1",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "settings/system-settings/store-details",
  //       input: <NoChild />,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "ERP 2",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "settings/system-settings/store-details",
  //       input: <NoChild />,
  //     },
  //   ],
  // },
  // {
  //   icon: <SmileIcon />,
  //   title: "Human Resources",
  //   desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //   path: "settings/system-settings",
  //   childrens: [
  //     {
  //       icon: <SmileIcon />,
  //       title: "Hardware Setup",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "settings/system-settings/store-details",
  //       input: <NoChild />,
  //     },
  //     {
  //       icon: <SmileIcon />,
  //       title: "Shift Setup",
  //       desc: "Customer settings, inventory, alerts,display, warehouse, Accounting",
  //       path: "settings/system-settings/store-details",
  //       input: <NoChild />,
  //     },
  //   ],
  // },
];

export default settingsData;
